/**
 * Context Variable service, backed by Firestore.
 * A variable is either personal (userId set) or org-global (isGlobal: true,
 * orgId set) — org-global variables are visible to the whole organisation.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { auth, db, orgIdFromEmail } from '../../../../shared/firebase';
import { ContextVariable } from '../models/ContextVariable';

const now = () => new Date().toISOString();

function requireUser() {
  const fbUser = auth.currentUser;
  if (!fbUser) throw new Error('Not signed in');
  return { uid: fbUser.uid, orgId: orgIdFromEmail(fbUser.email) };
}

const mapDoc = (data) =>
  ContextVariable.fromData({
    id: data.contextVariableId,
    title: data.title,
    context: data.context,
    userId: data.userId,
    organizationId: data.orgId,
    isGlobal: data.isGlobal,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  });

/**
 * Get all accessible context variables (user's own + global org variables)
 * @returns {Promise<Array<ContextVariable>>}
 */
export const getAllContextVariables = async () => {
  try {
    const { uid, orgId } = requireUser();
    const [ownSnap, orgSnap] = await Promise.all([
      getDocs(query(collection(db, 'contextVariables'), where('userId', '==', uid))),
      getDocs(
        query(
          collection(db, 'contextVariables'),
          where('orgId', '==', orgId),
          where('isGlobal', '==', true)
        )
      ),
    ]);
    const byId = new Map();
    ownSnap.forEach((d) => byId.set(d.id, d.data()));
    orgSnap.forEach((d) => byId.set(d.id, d.data()));
    return [...byId.values()]
      .sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))
      .map(mapDoc);
  } catch (error) {
    console.error('Error fetching context variables:', error);
    throw error;
  }
};

/**
 * Get single context variable by ID
 * @returns {Promise<ContextVariable>}
 */
export const getContextVariableById = async (id) => {
  try {
    const snap = await getDoc(doc(db, 'contextVariables', id));
    if (!snap.exists()) {
      throw new Error('Failed to fetch context variable');
    }
    return mapDoc(snap.data());
  } catch (error) {
    console.error('Error fetching context variable:', error);
    throw error;
  }
};

/**
 * Create new context variable
 * @returns {Promise<ContextVariable>}
 */
export const createContextVariable = async (data) => {
  try {
    const { uid, orgId } = requireUser();
    const contextVariableId = uuidv4();
    const docData = {
      contextVariableId,
      title: data.title,
      context: data.context,
      isGlobal: data.isGlobal || false,
      userId: uid,
      orgId,
      createdAt: now(),
      updatedAt: now(),
    };
    await setDoc(doc(db, 'contextVariables', contextVariableId), docData);
    return mapDoc(docData);
  } catch (error) {
    console.error('Error creating context variable:', error);
    throw error;
  }
};

/**
 * Update context variable
 * @returns {Promise<ContextVariable>}
 */
export const updateContextVariable = async (id, updates) => {
  try {
    const ref = doc(db, 'contextVariables', id);
    const fields = { updatedAt: now() };
    if (updates.title !== undefined) fields.title = updates.title;
    if (updates.context !== undefined) fields.context = updates.context;
    if (updates.isGlobal !== undefined) fields.isGlobal = updates.isGlobal;
    await updateDoc(ref, fields);
    const snap = await getDoc(ref);
    return mapDoc(snap.data());
  } catch (error) {
    console.error('Error updating context variable:', error);
    throw error;
  }
};

/**
 * Delete context variable
 * @returns {Promise<void>}
 */
export const deleteContextVariable = async (id) => {
  try {
    await deleteDoc(doc(db, 'contextVariables', id));
  } catch (error) {
    console.error('Error deleting context variable:', error);
    throw error;
  }
};
